/**
 * Canonical section labels for CV themes, resolved per locale.
 * Single source — JSON Resume themes re-export these labels.
 * Keys = CVEditingSection enum values (packages/shared/src/types/cv.ts)
 *
 * Translations come from the locale files (CV.* namespace).
 * When adding a locale: register it in translations.ts AND here.
 */
import { DEFAULT_LANGUAGE } from '../constants/language';
import arDZ from '../locales/ar_DZ.json';
import arMA from '../locales/ar_MA.json';
import de from '../locales/de.json';
import en from '../locales/en.json';
import es from '../locales/es.json';
import esMX from '../locales/es_MX.json';
import fr from '../locales/fr.json';
import pt from '../locales/pt.json';
import ro from '../locales/ro.json';
import tr from '../locales/tr.json';
import uk from '../locales/uk.json';

export type CvSectionLabels = Record<string, string>;

/**
 * Reference French labels (legacy, used by getSectionLabel).
 * Theme rendering goes through getSectionLabels(locale).
 */
export const CV_SECTION_LABELS: Record<string, string> = {
  personalInfos: 'Informations personnelles',
  competences: 'Compétences',
  skills: 'Compétences',
  languages: 'Langues',
  experiences: 'Expérience professionnelle',
  work: 'Expérience professionnelle',
  personalExperiences: 'Expériences personnelles',
  education: 'Formation',
  hobbiesAndInterests: "Centres d'intérêt",
  interests: "Centres d'intérêt",
  summary: 'À propos',
  complementaryInfo: 'Mentions complémentaires',
  strengths: 'Points forts',
  preferedTasks: 'Tâches préférées',
  notPreferedTasks: 'Tâches non préférées',
  contact: 'Contact',
  profiles: 'Profils',
  references: 'Références',
  awards: 'Mentions complémentaires',
  publications: 'Publications',
  projects: 'Projets',
  certificates: 'Certificats',
  volunteer: 'Expériences personnelles',
  courses: 'Cours',
  highlights: 'Points clés',
  phone: 'Téléphone',
  location: 'Ville',
  present: 'Présent',
} as const;

/**
 * Theme label key -> source key in the CV.* namespace of the locale files
 */
const SECTION_LABEL_SOURCES: Readonly<Record<string, string>> = {
  summary: 'summary',
  work: 'professionalExperience',
  experiences: 'professionalExperience',
  skills: 'skills',
  languages: 'languages',
  education: 'education',
  interests: 'hobbiesAndInterests',
  hobbiesAndInterests: 'hobbiesAndInterests',
  awards: 'complementaryInfo',
  complementaryInfo: 'complementaryInfo',
  personalExperiences: 'personalExperiences',
  volunteer: 'personalExperiences',
  contact: 'contact',
  phone: 'phone',
  location: 'location',
  publications: 'publications',
  references: 'references',
  projects: 'projects',
  certificates: 'certificates',
  profiles: 'profiles',
  present: 'present',
};

/**
 * Product locale (codeLong) -> CV namespace of the locale file.
 * Keep in sync with translations.ts.
 */
const LOCALE_CV: Readonly<Record<string, Record<string, unknown>>> = {
  'fr-FR': fr.CV,
  'en-GB': en.CV,
  'de-DE': de.CV,
  'es-ES': es.CV,
  'es-MX': esMX.CV,
  'pt-PT': pt.CV,
  'uk-UA': uk.CV,
  'ar-DZ': arDZ.CV,
  'ar-MA': arMA.CV,
  'tr-TR': tr.CV,
  'ro-RO': ro.CV,
};

/**
 * Localized section labels for a product locale (codeLong, e.g. 'es-MX').
 * No locale -> product default language.
 * Unknown locale or missing translation -> explicit error (no silent fallback).
 */
export function getSectionLabels(locale?: string): CvSectionLabels {
  const code = locale ?? DEFAULT_LANGUAGE;
  const cv = LOCALE_CV[code];
  if (!cv) {
    throw new Error(
      `CV section labels: unsupported locale "${code}". Supported: ${Object.keys(LOCALE_CV).join(', ')}. Add the locale file and register it in translations.ts and here.`,
    );
  }

  const labels: CvSectionLabels = {};
  for (const [labelKey, sourceKey] of Object.entries(SECTION_LABEL_SOURCES)) {
    const value = cv[sourceKey];
    if (typeof value !== 'string' || value.trim() === '') {
      throw new Error(
        `CV section labels: missing translation "CV.${sourceKey}" for locale "${code}" (label key "${labelKey}").`,
      );
    }
    labels[labelKey] = value;
  }
  return labels;
}

/**
 * Section label lookup by CVEditingSection id
 */
export function getSectionLabel(sectionId: string): string {
  return CV_SECTION_LABELS[sectionId] ?? sectionId;
}
